import { useTabActiveAndFocused } from "@/components/context-providers/GlobalIdleWrapper";
import { getTeam } from "@/gen_src/proto/chalk/server/v1/team-TeamService_connectquery";
import { Team } from "@/gen_src/proto/chalk/server/v1/team_pb";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { useDANGEROUSisChalkEmployee } from "@/utils/hooks/use-is-chalk-employee";
import { fixImpersonationCookies } from "@/utils/impersonation-utils";
import { Code } from "@connectrpc/connect";
import { useQuery } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import toast from "react-hot-toast";
import { useCallback, useEffect, useRef, useState } from "react";

interface PendingTeamCheck {
  id: number;
  minUpdatedAt: number;
}

type AutoImpersonationDecision = "wait" | "fix-impersonation" | "no-op";

/**
 * Automatically impersonates the current team if the user is a chalk employee. Ignores the global "admin mode"
 * setting. This is meant to be used only in the ImpersonationWrapper, which is intended to be a singleton, only
 * used at the root of the application, _app.tsx.
 */
export function useAutoImpersonate() {
  const { status } = useSession();
  const authenticated = status === "authenticated";

  const [pendingTeamCheck, setPendingTeamCheck] = useState<PendingTeamCheck>({
    id: 0,
    minUpdatedAt: 0,
  });
  const isChalkEmployee = useDANGEROUSisChalkEmployee({ ignoreAdminMode: true });
  const grpcQueryClient = useQueryClient();
  const { isReady: routeIsReady, projectId: urlProjectId } = useRouteInfo();
  const lastHandledTeamCheckId = useRef<number>(0);
  const shouldAutoImpersonate = isChalkEmployee && authenticated;

  const teamQuery = useQuery(getTeam, {}, { enabled: authenticated });
  const refetchTeam = teamQuery.refetch;
  const requestTeamCheck = useCallback((minUpdatedAt: number) => {
    setPendingTeamCheck((prev) => ({
      id: prev.id + 1,
      minUpdatedAt,
    }));
  }, []);

  const requestFreshTeamCheck = useCallback(() => {
    if (!shouldAutoImpersonate || !routeIsReady) {
      return;
    }

    requestTeamCheck(Date.now());
    void refetchTeam();
  }, [refetchTeam, requestTeamCheck, routeIsReady, shouldAutoImpersonate]);

  useTabActiveAndFocused(requestFreshTeamCheck, 0);

  useEffect(() => {
    requestFreshTeamCheck();
  }, [requestFreshTeamCheck, urlProjectId]);

  useEffect(() => {
    async function runEffect() {
      if (!shouldAutoImpersonate || !routeIsReady) {
        return;
      }

      const resultUpdatedAt = teamQuery.error
        ? teamQuery.errorUpdatedAt
        : teamQuery.data
          ? teamQuery.dataUpdatedAt
          : null;

      if (lastHandledTeamCheckId.current === pendingTeamCheck.id) {
        return;
      }

      const decision = getAutoImpersonationDecision({
        team: teamQuery.data?.team,
        errorCode: teamQuery.error?.code,
        urlProjectId,
        resultUpdatedAt,
        minUpdatedAt: pendingTeamCheck.minUpdatedAt,
      });
      if (decision === "wait") {
        return;
      }

      lastHandledTeamCheckId.current = pendingTeamCheck.id;
      if (decision === "fix-impersonation") {
        const didSetCookie = await fixImpersonationCookies(grpcQueryClient, urlProjectId);
        if (!didSetCookie) {
          return;
        }

        const refreshedTeam = await refetchTeam();
        const teamName = refreshedTeam.data?.team?.name;
        toast.success(teamName ? `Now impersonating ${teamName}` : "Impersonation updated");
      }
    }
    void runEffect();
  }, [
    grpcQueryClient,
    pendingTeamCheck.id,
    pendingTeamCheck.minUpdatedAt,
    refetchTeam,
    shouldAutoImpersonate,
    teamQuery.data,
    teamQuery.dataUpdatedAt,
    teamQuery.error,
    teamQuery.errorUpdatedAt,
    routeIsReady,
    urlProjectId,
  ]);
}

function getAutoImpersonationDecision({
  team,
  errorCode,
  urlProjectId,
  resultUpdatedAt,
  minUpdatedAt,
}: {
  team: Team | undefined;
  errorCode: Code | undefined;
  urlProjectId: string | undefined;
  resultUpdatedAt: number | null;
  minUpdatedAt: number;
}): AutoImpersonationDecision {
  if (resultUpdatedAt == null) {
    return "wait";
  }

  if (errorCode === Code.PermissionDenied) {
    // In practice, most cases that should lead to auto-impersonation will cause
    // the team query to return PermissionDenied. Whether a different subdomain
    // leads to an error depends on whether there is already an impersonation cookie.
    //
    // Cases:
    // - go to environment-scoped page for a team that is not yours in the same subdomain. You MAY or MAY NOT have an impersonation cookie
    // - go to a different subdomain's unscoped, project-scoped, or environment-scoped page for a team that is not yours
    //    with an impersonation cookie from a different subdomain
    // - go to a different subdomain's environment-scoped page with NO impersonation cookie
    return "fix-impersonation";
  }

  if (
    team != null &&
    urlProjectId != null &&
    !team.projects.some((project) => project.id === urlProjectId)
  ) {
    // If the team query succeeds, an unscoped page cannot prove we wanted to
    // impersonate a different team. A project-scoped page can: if the URL
    // project is not in the returned team, the cookie points at the wrong team.
    //
    // Cases:
    // - go to a purely project-scoped page for a team that is not yours in the same subdomain. You MAY or MAY NOT have an impersonation cookie
    // - go to a different subdomain's purely project-scoped page for a team that is not yours WITHOUT an impersonation cookie
    return "fix-impersonation";
  }

  // A cached "wrong team" or PermissionDenied result is enough to fix immediately.
  // A cached "looks fine" result is not enough, because cookies can change in another tab.
  if (resultUpdatedAt < minUpdatedAt) {
    return "wait";
  }

  return "no-op";
}
